import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { UpdateAttendeeTimeGQL } from 'src/generated/graphql.generated';

import { GraphQLModule } from '../../../../core/auth/graphql.module';

import { NavBaseComponent } from './nav-base';

/**
 * Crew roles that appear in the roll call.
 * Excludes 'Author' (the job creator) and 'customer'.
 */
const CREW_ROLES = new Set([
    'Crew Lead', 'Assistant Crew Lead', 'Crew Member', 'Driver',
]);

type AttendeeStatus = 'unknown' | 'present' | 'late' | 'absent';

interface CrewMember {
    userId: string;
    name: string;
    role: string;
    status: AttendeeStatus;
    /** Unix seconds recorded when marked present/late */
    startTime: number | null;
    /** Display time string shown after recording */
    startTimeDisplay: string;
    saving: boolean;
    saveError: string;
}

@Component({
    selector: 'app-nav-crew-roll-call',
    standalone: true,
    imports: [CommonModule, FormsModule, GraphQLModule],
    styles: [`
        :host { display: block; margin-bottom: 1rem; }

        .crew-card {
            background: white; border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.07); overflow: hidden;
        }
        .crew-header {
            background: #1a1a1a; color: white;
            padding: 0.85rem 1.1rem;
            display: flex; align-items: center; justify-content: space-between;
        }
        .crew-header-left { display: flex; align-items: center; gap: 0.5rem; }
        .crew-header-title { font-size: 0.85rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; }
        .crew-count { font-size: 0.8rem; opacity: 0.7; }

        .member-row {
            padding: 0.85rem 1.1rem;
            border-bottom: 1px solid #f0f0f0;
        }
        .member-row:last-child { border-bottom: none; }

        .member-top {
            display: flex; align-items: center; gap: 0.85rem; margin-bottom: 0.6rem;
        }
        .avatar {
            width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0;
            background: #e8edf2;
            display: flex; align-items: center; justify-content: center;
            font-size: 0.85rem; font-weight: 700; color: #5a7a9a;
        }
        .avatar.lead { background: #1a1a1a; color: white; }
        .member-info { flex: 1; min-width: 0; }
        .member-name { font-size: 1rem; font-weight: 600; color: #1a1a1a; }
        .member-role { font-size: 0.78rem; color: #888; margin-top: 0.1rem; }

        /* Status chips */
        .status-chips { display: flex; gap: 0.4rem; flex-wrap: wrap; }
        .chip {
            padding: 0.4rem 0.75rem; border-radius: 20px;
            border: 1.5px solid #e0e0e0; background: white;
            font-size: 0.82rem; cursor: pointer; font-family: inherit; font-weight: 500;
            transition: all 0.12s;
        }
        .chip:active { transform: scale(0.96); }
        .chip.chip-present.active  { border-color: #2d6a2d; background: #f0f7f0; color: #2d6a2d; font-weight: 700; }
        .chip.chip-late.active     { border-color: #d97706; background: #fffbeb; color: #d97706; font-weight: 700; }
        .chip.chip-absent.active   { border-color: #dc2626; background: #fef2f2; color: #dc2626; font-weight: 700; }
        .chip.chip-present:not(.active):hover { border-color: #2d6a2d; color: #2d6a2d; }
        .chip.chip-late:not(.active):hover    { border-color: #d97706; color: #d97706; }
        .chip.chip-absent:not(.active):hover  { border-color: #dc2626; color: #dc2626; }

        /* Start time row */
        .start-time-row {
            display: flex; align-items: center; gap: 0.5rem;
            margin-top: 0.5rem; padding-top: 0.5rem;
            border-top: 1px dashed #f0f0f0;
            font-size: 0.82rem;
        }
        .start-time-badge {
            display: flex; align-items: center; gap: 0.3rem;
            color: #2d6a2d; font-weight: 600;
        }
        .saving-indicator { color: #888; }
        .save-error { color: #dc2626; flex: 1; }

        /* Footer warning */
        .footer-warning {
            background: #fff7ed; padding: 0.75rem 1.1rem;
            border-top: 1px solid #fed7aa;
            font-size: 0.85rem; color: #9a3412;
            display: flex; align-items: center; gap: 0.5rem;
        }

        .no-crew { padding: 1.25rem; text-align: center; color: #aaa; font-size: 0.9rem; }
    `],
    template: `
        <div class="crew-card">
            <div class="crew-header">
                <div class="crew-header-left">
                    <i class="pi pi-users" style="font-size:1rem;"></i>
                    <span class="crew-header-title">Crew Check-In</span>
                </div>
                <span class="crew-count">{{ presentCount }}/{{ members.length }} confirmed</span>
            </div>

            <div class="no-crew" *ngIf="members.length === 0">
                No crew assigned to this event. Crew is assigned on the
                <strong>Schedule &rsaquo; Dispatch</strong> page.
            </div>

            <div class="member-row" *ngFor="let m of members">
                <div class="member-top">
                    <div class="avatar" [class.lead]="isLead(m.role)">{{ initials(m.name) }}</div>
                    <div class="member-info">
                        <div class="member-name">{{ m.name }}</div>
                        <div class="member-role">{{ m.role }}</div>
                    </div>
                </div>

                <div class="status-chips">
                    <button class="chip chip-present" [class.active]="m.status === 'present'"
                        (click)="setStatus(m, 'present')">✓ Present</button>
                    <button class="chip chip-late" [class.active]="m.status === 'late'"
                        (click)="setStatus(m, 'late')">⏱ Late</button>
                    <button class="chip chip-absent" [class.active]="m.status === 'absent'"
                        (click)="setStatus(m, 'absent')">✗ Not Here</button>
                </div>

                <div class="start-time-row" *ngIf="m.status === 'present' || m.status === 'late'">
                    <ng-container *ngIf="m.startTime">
                        <span class="start-time-badge">
                            <i class="pi pi-clock"></i> Started {{ m.startTimeDisplay }}
                        </span>
                    </ng-container>
                    <span class="saving-indicator" *ngIf="m.saving">
                        <i class="pi pi-spin pi-spinner"></i> Saving...
                    </span>
                    <span class="save-error" *ngIf="m.saveError">{{ m.saveError }}</span>
                </div>
            </div>

            <div class="footer-warning" *ngIf="hasUnknown">
                <i class="pi pi-exclamation-triangle"></i>
                Mark everyone's status before continuing.
            </div>
        </div>
    `,
})
export class NavCrewRollCallComponent extends NavBaseComponent implements OnInit {

    members: CrewMember[] = [];

    private updateAttendeeTimeGQL = inject(UpdateAttendeeTimeGQL);

    constructor() { super(); }

    get presentCount(): number {
        return this.members.filter(m => m.status === 'present' || m.status === 'late').length;
    }

    get hasUnknown(): boolean {
        return this.members.some(m => m.status === 'unknown');
    }

    /**
     * An empty crew list **blocks** this step — a product decision (P1), not a default.
     *
     * The reasoning: a move with nobody assigned is an incomplete job setup, and the crew
     * cannot start one. Note this is the opposite of the rule the other gated components use
     * for an empty list (see #19), so the two are deliberately inconsistent until P2 settles
     * the same question for inventory.
     *
     * Blocking is only defensible because the empty state now tells the reader where to go.
     * If that message is ever removed, this becomes a dead end again.
     */
    get isSatisfied(): boolean {
        return this.members.length > 0 && !this.hasUnknown;
    }

    ngOnInit() {
        console.log('[NavCrewRollCall] node.props:', this.node?.props);
        const attendees: any[] = this.node.props?.attendees ?? [];
        this.members = attendees
            .filter(a => {
                const role: string = a.role ?? '';
                return CREW_ROLES.has(role)
                    || role.toLowerCase().includes('crew')
                    || role.toLowerCase().includes('driver');
            })
            .map(a => ({
                userId: a.userId ?? '',
                name: a.name ?? 'Crew Member',
                role: a.role ?? 'Crew Member',
                status: 'unknown' as AttendeeStatus,
                startTime: a.startTime ?? null,
                startTimeDisplay: a.startTime
                    ? new Date(a.startTime * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '',
                saving: false,
                saveError: '',
            }));

        // Report the starting state to the step gate rather than leaving it at the
        // registered default.
        //
        // Note this currently emits `false` in every reachable state — an empty crew blocks
        // by design (P1), and a populated crew starts with everyone `unknown`. So it changes
        // nothing today; it is here so the gate reflects a real answer rather than an assumed
        // one, and so a future change to `isSatisfied` (remembering statuses across a resume,
        // say) takes effect without anyone remembering to add this call. The three other
        // gated components do the same, and for two of them it *is* load-bearing. See #19.
        this._notifyGate();
    }

    private _notifyGate() {
        if (typeof this.node.props?.onReady === 'function') {
            this.node.props.onReady(this.isSatisfied);
        }
    }

    isLead(role: string): boolean {
        return role === 'Crew Lead' || role === 'Assistant Crew Lead';
    }

    initials(name: string): string {
        return name.trim().split(/\s+/).map(p => p[0]).join('').toUpperCase().slice(0, 2);
    }

    setStatus(m: CrewMember, status: AttendeeStatus) {
        const prev = m.status;
        m.status = status;

        // Auto-record start time when marked present/late for the first time
        if ((status === 'present' || status === 'late') && !m.startTime) {
            this.recordStartTime(m);
        }

        // If switching back to absent/unknown after being present, clear the start time
        if ((status === 'absent') && prev !== 'absent' && m.startTime) {
            m.startTime = null;
            m.startTimeDisplay = '';
        }

        this._notifyGate();
    }

    private recordStartTime(m: CrewMember) {
        const eventId: string = this.node.props?.eventId ?? this.node.props?.appointmentId ?? '';
        if (!eventId || !m.userId) return;

        const nowSeconds = Math.floor(Date.now() / 1000);
        m.saving = true;
        m.saveError = '';

        this.updateAttendeeTimeGQL.mutate({
            eventId,
            userId: m.userId,
            startTime: nowSeconds,
        }).subscribe({
            next: () => {
                m.startTime = nowSeconds;
                m.startTimeDisplay = new Date(nowSeconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                m.saving = false;
            },
            error: (err) => {
                console.error('[NavCrewRollCall] Failed to record start time:', err);
                m.saveError = 'Failed to save — try again';
                m.saving = false;
            },
        });
    }
}
